import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router";

import type { CreateGroupChatRequest, UserDto, UserExtendedDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { UsersPlusIcon } from "@/components/Icons/UsersPlusIcon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { User } from "@/components/User/User";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import {
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
  createTrimmedMaxLengthStringRule,
  createTrimmedMinLengthStringRule,
} from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useSlug } from "@/hooks/useSlug";
import { chatMutations } from "@/queries/chats/mutations";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export const MIN_GROUP_SEARCH_CHARACTERS = 2;
export const GROUP_NAME_MIN_LENGTH = 3;
export const GROUP_NAME_MAX_LENGTH = 40;
const FORM_ID = "create-chat-modal-form";

interface CreateChatFormValues {
  groupMembers: UserDto[];
  groupName: string;
  groupImage?: FormImage[];
}
enum Step {
  CreatePrivateChat,
  CreateGroupChatSelectMembers,
  CreateGroupChatConfirm,
}

type CreateChatModalProps = ModalBaseProps;

export function CreateChatModal({ isOpened, onOpenChange }: CreateChatModalProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const navigate = useNavigate();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const [step, setStep] = useState<Step>(Step.CreatePrivateChat);
  const [query, setQuery] = useState<string>("");
  const chatQueries = useChatQueries();
  const formMethods = useForm<CreateChatFormValues>({
    defaultValues: {
      groupMembers: [],
      groupName: "",
    },
  });
  const debouncedQuery = useDebounceValue(query.trim().length < MIN_GROUP_SEARCH_CHARACTERS ? "" : query);

  const {
    data: availableUsersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchMoreUsers,
    isLoading: isLoadingUsers,
    isFetchingNextPage: isFetchingMoreUsers,
  } = useInfiniteQuery({
    ...chatQueries.listAvailableUsersInfinite(debouncedQuery),
    enabled: isOpened,
  });
  const createGroupChatMutation = chatMutations.useCreateGroupChat();
  const amountTotalUsers = useMemo(() => availableUsersData?.pages[0]?.total ?? 0, [availableUsersData]);
  const availableUsers = useMemo(() => availableUsersData?.pages.flatMap((x) => x.items) ?? [], [availableUsersData]);
  const selectedUsers = useWatch({
    control: formMethods.control,
    name: "groupMembers",
  });

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreUsers && hasMoreUsers) {
        void fetchMoreUsers();
      }
    }, [fetchMoreUsers, hasMoreUsers, isFetchingMoreUsers]),
  });

  const handlePrevStep = (prevStep: number) => {
    if (prevStep === 0) {
      formMethods.reset();
      setQuery("");
    }
    setStep(prevStep);
  };

  const handleNextStep = (nextStep: number) => {
    setQuery("");
    setStep(nextStep);
  };

  const onToggleSelectUser = (checked: boolean, user: UserDto) => {
    if (checked) {
      formMethods.setValue("groupMembers", [...selectedUsers, user]);
    } else {
      formMethods.setValue(
        "groupMembers",
        selectedUsers.filter((selectedUser) => selectedUser.id !== user.id),
      );
    }
  };

  const handleSubmitCreateGroupChat = async (values: CreateChatFormValues) => {
    if (step !== Step.CreateGroupChatConfirm || values.groupMembers.length === 0 || isSubmitting) {
      return;
    }

    const name = values.groupName.trim();
    if (name.length < GROUP_NAME_MIN_LENGTH || name.length > GROUP_NAME_MAX_LENGTH) {
      return;
    }

    try {
      const payload: CreateGroupChatRequest = {
        name,
        userIds: values.groupMembers.map((x) => x.id),
      };
      if (values.groupImage && values.groupImage.length > 0) {
        const image = await uploadFormImage(values.groupImage[0]);
        payload.imageId = image?.id;
      }
      await createGroupChatMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          onOpenChange(false);
          void navigate(routes.chats.details({ slug, id: data.id }));
        },
      });
    } catch {
      // Upload and creation mutations display their failure feedback. Keep the wizard open for retry.
    }
  };

  const isSubmitting = createGroupChatMutation.isPending || isUploadingImage;
  let title = "";
  switch (step) {
    case Step.CreatePrivateChat:
      title = t("page.chats.modal.new-chat.start-title");
      break;
    case Step.CreateGroupChatSelectMembers:
      title = t("page.chats.modal.new-chat.group-chat.select-member.title");
      break;
    case Step.CreateGroupChatConfirm:
      title = t("page.chats.modal.new-chat.group-chat.group-details.title");
      break;
  }

  return (
    <Modal.Root
      data-testid="chats-user-search-modal"
      {...{
        title,
        isOpened,
        onOpenChange,
      }}
      shouldCloseOnClickOutside={false}
      onExitComplete={() => {
        formMethods.reset();
        setQuery("");
        setStep(0);
      }}
    >
      <Modal.Body>
        <Form.Root
          id={FORM_ID}
          fullWidth
          gap="md"
          maxHeight="parent"
          onSubmit={handleSubmitCreateGroupChat}
          {...{
            formMethods,
          }}
        >
          {step === Step.CreatePrivateChat && (
            <>
              <SearchInput
                data-testid="search-input"
                onChange={(e) => setQuery(e.target.value.trim())}
                placeholder={
                  sessionUser.project.type === "addressBased"
                    ? t("page.chats.modal.new-chat.input-placeholder.address-based")
                    : t("page.chats.modal.new-chat.input-placeholder.company-based")
                }
              />
              <Stack gap="sm">
                {amountTotalUsers > 0 && (
                  <span className="text-caption-emphasis">
                    {t("page.chats.modal.new-chat.total-results", {
                      count: amountTotalUsers,
                    })}
                  </span>
                )}
                <PrivateChatUserList
                  isLoading={isLoadingUsers}
                  users={availableUsers}
                  hasMoreUsers={hasMoreUsers}
                  loaderRef={ref}
                />
              </Stack>
            </>
          )}
          {step === Step.CreateGroupChatSelectMembers && (
            <>
              {/* Search */}
              <SearchInput
                data-testid="search-input"
                onChange={(e) => setQuery(e.target.value.trim())}
                placeholder={
                  sessionUser.project.type === "addressBased"
                    ? t("page.chats.modal.new-chat.input-placeholder.address-based")
                    : t("page.chats.modal.new-chat.input-placeholder.company-based")
                }
              />
              {selectedUsers.length > 0 && (
                <Card borderTone="secondary" density="flush">
                  <ul className="flex h-fit max-w-full gap-space-lg overflow-x-auto p-space-md">
                    {selectedUsers.map((user) => (
                      <Stack as="li" key={user.id} gap="sm" align="center">
                        <span className="relative">
                          <UserAvatar image={user.avatar} />
                          <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3">
                            <IconButton
                              title={t("common.action.delete")}
                              onClick={() =>
                                formMethods.setValue(
                                  "groupMembers",
                                  selectedUsers.filter((x) => x.id !== user.id),
                                )
                              }
                              variant="filled"
                              tone="error"
                              withTooltip={false}
                              size="xs"
                              isCircular
                              icon={<XCloseIcon />}
                            />
                          </span>
                        </span>
                        <span className="text-overline">{user.firstName}</span>
                      </Stack>
                    ))}
                  </ul>
                </Card>
              )}
              <span className="text-caption-emphasis">
                {t("page.chats.modal.new-chat.total-results", {
                  count: amountTotalUsers,
                })}
              </span>
              {/* Loading state */}
              {isLoadingUsers && <Spinner size="md" />}
              {/* Empty state */}
              {!isLoadingUsers && amountTotalUsers === 0 && (
                <p className="text-caption text-text-secondary">{t("page.chats.modal.new-chat.no-results")}</p>
              )}
              {/* Contentful state */}
              {!isLoadingUsers && availableUsers.length > 0 && (
                <Card as="ul" density="flush" borderTone="secondary" scroll="compact">
                  {availableUsers.map((user) => {
                    const isUserSelected = !!selectedUsers.find((selectedUser) => selectedUser.id === user.id);

                    return (
                      <li
                        key={user.id}
                        data-testid="available-user"
                        className="cursor-pointer border-b border-b-border-secondary last:border-b-0 hover:bg-background-brand-light"
                      >
                        <label
                          htmlFor={`user-${user.id}`}
                          className="flex w-full cursor-pointer items-center justify-between p-space-md"
                        >
                          <span className="flex grow items-center gap-space-sm">
                            <UserAvatar image={user.avatar} />
                            <address className="text-caption not-italic">
                              <span className="text-caption-emphasis text-text-primary">{user.fullName}</span>
                              <p className="text-text-secondary">
                                <span>{user.locatedAt}</span>
                              </p>
                            </address>
                          </span>
                          <Checkbox
                            id={`user-${user.id}`}
                            name="groupMembers"
                            checked={isUserSelected}
                            onChange={(e) => onToggleSelectUser(e.target.checked, user)}
                            shape="rounded"
                          />
                        </label>
                      </li>
                    );
                  })}
                  {hasMoreUsers && (
                    <li className="flex justify-center p-space-md" ref={ref}>
                      <Spinner size="md" />
                    </li>
                  )}
                </Card>
              )}
            </>
          )}
          {step === Step.CreateGroupChatConfirm && (
            <>
              <Stack gap="sm">
                <span className="self-center">
                  <FormField label={t("component.group-chat-info-modal.form.image.label")} hideLabel>
                    <FormImageInput<CreateChatFormValues, "groupImage">
                      name="groupImage"
                      rules={{
                        validate: {
                          size: createImageSizeRule({
                            t,
                          }),
                          type: createImageTypeRule({
                            t,
                          }),
                        },
                      }}
                      nOfImages={1}
                      isAvatarInput
                      cropper
                    />
                  </FormField>
                </span>
                <FormField label={t("page.chats.modal.new-chat.group.group-name")} required>
                  <FormInput<CreateChatFormValues, "groupName">
                    name="groupName"
                    placeholder={t("page.chats.modal.new-chat.group.group-name.placeholder")}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "page.chats.modal.new-chat.group.group-name"),
                        minLength: createTrimmedMinLengthStringRule({
                          t,
                          fieldName: "page.chats.modal.new-chat.group.group-name",
                          minLength: GROUP_NAME_MIN_LENGTH,
                        }),
                        maxLength: createTrimmedMaxLengthStringRule({
                          t,
                          fieldName: "page.chats.modal.new-chat.group.group-name",
                          maxLength: GROUP_NAME_MAX_LENGTH,
                        }),
                      },
                    }}
                  />
                </FormField>
              </Stack>
              <Stack gap="sm">
                <span className="text-caption-emphasis">
                  {t("page.chats.modal.new-chat.group.selected-members", {
                    count: selectedUsers.length,
                  })}
                </span>
                <Card borderTone="secondary" density="flush">
                  <ul className="grid size-full grid-cols-6 overflow-y-auto p-space-md">
                    {selectedUsers.map((user) => (
                      <Stack as="li" key={user.id} gap="sm" align="center">
                        <span className="relative">
                          <UserAvatar image={user.avatar} />
                          <span className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3">
                            <IconButton
                              size="xs"
                              title={t("common.action.delete")}
                              onClick={() =>
                                formMethods.setValue(
                                  "groupMembers",
                                  selectedUsers.filter((x) => x.id !== user.id),
                                )
                              }
                              variant="filled"
                              tone="error"
                              withTooltip={false}
                              isCircular
                              icon={<XCloseIcon />}
                            />
                          </span>
                        </span>
                        <span className="text-overline">{user.firstName}</span>
                      </Stack>
                    ))}
                  </ul>
                </Card>
              </Stack>
            </>
          )}
        </Form.Root>
      </Modal.Body>
      <Modal.Actions>
        {step === Step.CreatePrivateChat && (
          <Button
            data-testid="new-group-chat-btn"
            variant="filled"
            fullWidth
            onClick={() => handleNextStep(1)}
            icon={<UsersPlusIcon />}
          >
            {t("page.chats.modal.new-chat.group-chat.button")}
          </Button>
        )}
        {step === Step.CreateGroupChatSelectMembers && (
          <>
            <Button variant="outline" onClick={() => handlePrevStep(0)}>
              {t("common.action.back")}
            </Button>
            <Button
              data-testid="next-step-btn"
              variant="filled"
              disabled={selectedUsers.length === 0}
              onClick={() => handleNextStep(2)}
            >
              {t("common.action.next")}
            </Button>
          </>
        )}
        {step === Step.CreateGroupChatConfirm && (
          <>
            <Button variant="outline" onClick={() => handlePrevStep(1)}>
              {t("common.action.back")}
            </Button>
            <Button
              type="submit"
              variant="filled"
              form={FORM_ID}
              disabled={selectedUsers.length === 0}
              isLoading={isSubmitting}
            >
              {t("common.action.create")}
            </Button>
          </>
        )}
      </Modal.Actions>
    </Modal.Root>
  );
}

type PrivateChatUserListProps = {
  isLoading: boolean;
  users: UserExtendedDto[];
  hasMoreUsers: boolean;
  loaderRef: (node: Element | null) => void;
};

function PrivateChatUserList({ isLoading, users, hasMoreUsers, loaderRef }: PrivateChatUserListProps): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();

  if (isLoading) {
    return (
      <section className="flex h-scroll-area-compact items-center justify-center">
        <Spinner size="md" />
      </section>
    );
  }

  if (users.length === 0) {
    return (
      <section className="flex h-scroll-area-compact items-center justify-center">
        <p className="px-space-sm pt-space-xs pb-space-md text-caption text-text-secondary">
          {t("page.chats.modal.new-chat.no-results")}
        </p>
      </section>
    );
  }

  return (
    <Card as="ul" density="flush" scroll="compact">
      {users.map((availableUser) => (
        <li
          key={availableUser.id}
          data-testid="available-user"
          className="border-b border-b-border-default last:border-b-0"
        >
          <Link
            to={routes.users.details({ slug, id: availableUser.id })}
            className="flex w-full p-space-md hover:bg-background-secondary"
          >
            <User user={availableUser} renderSubtitle={(user) => user.locatedAt} shouldNavigate={false} />
          </Link>
        </li>
      ))}
      {hasMoreUsers && (
        <li className="flex justify-center p-space-md" ref={loaderRef}>
          <Spinner size="md" />
        </li>
      )}
    </Card>
  );
}
